// KISSED homepage content — DEFAULTS used as fallback and as the seed for the
// Website Manager. Published overrides are fetched from /api/site-content/homepage.
// HOME_VERSION 2 = concern-led conversion layout. Content published under the
// earlier layout (identical to the old defaults) is not applied to the new one.
export const HOME_VERSION = 4;

export const IMG = {
  practitionerMicroscope: "/media/kissed/dr-christy-trichology.jpg",
  practitionerPortrait: "/media/kissed/dr-christy-hero.jpg",
  practitionerHeadshot: "/media/kissed/dr-christy-headshot.jpg",
};

export const DEFAULT_HOME = {
  version: HOME_VERSION,
  hero: {
    visible: true,
    eyebrow: "Trichology & Functional Wellness Clinic · Raleigh, NC",
    headline: "Hair thinning, scalp concerns, or wellness questions?",
    headlineAccent: "Start with a clearer plan.",
    subcopy: "KISSED helps you explore possible contributing factors and understand your next steps through detailed assessments, personalized recommendations, and appropriate in-clinic care.",
    note: "Virtual assessments available from home · In-clinic care in Raleigh, NC",
    primaryLabel: "Start My New Client Assessment",
    primaryTo: "/navigator",
    secondaryLabel: "Explore Paid Virtual Assessments",
    secondaryTo: "/wellness#choose-depth",
    image: IMG.practitionerMicroscope,
  },
  trust: {
    visible: true,
    eyebrow: "Meet Your Practitioner",
    image: IMG.practitionerPortrait,
    aboutLabel: "Read Dr. C's full background",
    aboutTo: "/about",
  },
  concerns: {
    visible: true,
    eyebrow: "Start With Your Concern",
    heading: "What brings you to KISSED?",
    sub: "Choose what sounds most like you.",
    items: [
      { t: "Thinning edges or a widening part", d: "Changes along the hairline or part can have more than one contributing factor. We look at your history, hair-care practices and wellness patterns together.", btn: "See the hair & scalp pathway", to: "/hair-scalp" },
      { t: "Increased shedding or less density", d: "If you are seeing more hair in the shower or your ponytail feels thinner, an assessment helps organize what has changed and when.", btn: "Start my assessment", to: "/navigator" },
      { t: "Dry, itchy, flaky or uncomfortable scalp", d: "Scalp discomfort is often best seen directly. A magnified, in-person look helps your practitioner understand what is happening on the surface.", btn: "View In-Clinic Services", to: "/in-clinic-services" },
      { t: "Energy, sleep, stress, digestion or weight changes", d: "When several things change at once, looking at one symptom at a time may not tell the whole story. Our functional wellness assessments look at the patterns together.", btn: "Explore virtual assessments", to: "/wellness#choose-depth" },
    ],
  },
  how: {
    visible: true,
    eyebrow: "How Care Works",
    heading: "Five clear steps, from first questions to ongoing care.",
    steps: [
      { t: "Complete the new client assessment", d: "A short, free set of questions about your main concern, how long it has been happening and how you prefer to begin." },
      { t: "Receive your recommended pathway", d: "You'll see the virtual assessment depth or in-clinic visit that best matches your answers." },
      { t: "Purchase a virtual assessment or book in-clinic", d: "Buy a paid virtual assessment, or schedule an in-clinic assessment directly — a virtual purchase is not required first." },
      { t: "Receive findings and recommendations", d: "You receive the findings and recommendations included in the service you purchased." },
      { t: "Continue your care", d: "Next steps may include a released protocol, a consultation, in-clinic care or appropriate products — each offered separately." },
    ],
    note: "The new client assessment is free and only recommends a starting point. A paid virtual assessment is a detailed online assessment with findings. A consultation is a separate live conversation booked with the clinic.",
  },
  tiers: {
    visible: true,
    eyebrow: "Paid Virtual Assessments",
    heading: "KISSED Wellness Assessment™ — choose your depth.",
    sub: "Completed online from home through our secure client system. Each level includes everything in the level before it. Compare levels and pricing on the next page.",
    who: [
      { key: "intro", who: "You want a focused starting point before going deeper." },
      { key: "standard", who: "You want the bigger picture, with herbal and lifestyle guidance and a PDF report." },
      { key: "premium", who: "You want the most comprehensive guidance, including a priority clinical review." },
    ],
    note: "Each new assessment is a separate purchase. Previously purchased assessments remain listed in your Client Care Hub.",
  },
  inclinic: {
    visible: true,
    eyebrow: "In-Clinic Services · Raleigh, NC",
    heading: "When an in-person look is the right next step.",
    sub: "You can book an appropriate in-clinic assessment directly — a virtual assessment is not required first. Scalp treatments are planned only after an assessment.",
    visit: [
      "Review of your assessment answers and history",
      "Appropriate testing for the service you booked",
      "Scalp photographs and microscopic imaging where included",
      "A discussion of what was observed",
      "Recommended next steps",
    ],
    hsaNote: "HSA/FSA cards accepted for eligible services. Eligibility varies by plan and service.",
    allLabel: "See all In-Clinic Services",
    allTo: "/in-clinic-services",
  },
  continuing: {
    visible: true,
    eyebrow: "Continuing Care",
    heading: "What can come after your assessment.",
    items: [
      { t: "Personalized care plans & programs", d: "Structured programs built around your own findings, with progress you can follow in your Client Care Hub.", btn: "View Programs", to: "/programs" },
      { t: "Scalp therapies & progress reviews", d: "Hands-on in-clinic care and follow-up visits when your plan calls for them.", btn: "View In-Clinic Services", to: "/in-clinic-services" },
      { t: "Functional formulas & products", d: "Practitioner-selected herbal formulas, teas and scalp essentials.", btn: "Visit the Shop", to: "/shop" },
    ],
    note: "Assessment fees cover only the inclusions listed for that assessment. Consultations, in-clinic services, programs and products are priced and purchased separately.",
  },
  faq: {
    visible: true,
    eyebrow: "Questions",
    heading: "Frequently asked questions",
    items: [
      { q: "Where should a new client begin?", a: "Start with the free New Client Assessment. It takes a few minutes and recommends whether a virtual assessment or an in-clinic visit is the better starting point." },
      { q: "What is the difference between a virtual assessment and in-clinic care?", a: "A virtual assessment is a paid, detailed assessment you complete online from home. In-clinic care takes place at our Raleigh, NC clinic and is booked through our online scheduler. You do not need to buy a virtual assessment before booking an appropriate in-clinic assessment." },
      { q: "What does a paid assessment include?", a: "Intro includes the adaptive assessment, core findings and summary recommendations. Standard adds herbal suggestions, lifestyle recommendations and a downloadable PDF report. Premium adds expanded protocol recommendations, aligned KISSED service recommendations and a priority clinical review with the KISSED team." },
      { q: "Is a live consultation included?", a: "Intro and Standard are completed online and do not include a live consultation. Premium includes a priority clinical review with the KISSED team. Consultations are booked separately." },
      { q: "What costs extra?", a: "Consultations, in-clinic services, programs and products are priced separately. An assessment purchase includes only the items listed for that level." },
      { q: "Do you accept HSA/FSA cards?", a: "HSA/FSA cards accepted for eligible services. Eligibility varies by plan and service." },
      { q: "How are findings and plans delivered?", a: "Findings and recommendations are delivered to the email on file, and Standard and Premium include a downloadable PDF report. Programs assigned to you appear in your Client Care Hub." },
      { q: "How does a returning client purchase another assessment?", a: "Sign in to your Client Care Hub, or choose a level on this page. Each new assessment is a separate purchase, and your earlier assessments stay listed in your Care Hub." },
      { q: "When should I expect my report?", a: "Your confirmation email explains what happens next for the level you purchased." },
    ],
  },
  closing: {
    visible: true,
    heading: "Not sure where to start? Start here.",
    copy: "Answer a few questions and we'll point you to the right virtual assessment or in-clinic visit.",
    ctaLabel: "Start My New Client Assessment",
    ctaTo: "/navigator",
    secondaryLabel: "Client Care Hub",
    secondaryTo: "/client/login",
  },
};

// Section-level merge; content saved under an older layout version is ignored.
export function mergeHome(published) {
  const out = { ...DEFAULT_HOME };
  if (published && typeof published === "object" && published.version === HOME_VERSION) {
    for (const k of Object.keys(DEFAULT_HOME)) {
      if (published[k] && typeof published[k] === "object") out[k] = published[k];
    }
  }
  return out;
}
